/**
 * MentionText — renders plain text with @Name spans converted to clickable chips.
 *
 * Usage:
 *   <MentionText text={comment} />
 *
 * Behaviour:
 *  - Pulls user list from /api/users (5-min module cache shared with MentionTextarea)
 *  - Greedy-matches `@<name>` against known user full names (longest match wins)
 *  - Renders each mention as a violet pill; click opens a small Popover with
 *    name + email + role + a 'View profile' action
 *  - Unknown @tokens fall back to subtle plain text so we never lose content
 */
import { useEffect, useState, useMemo } from 'react';
import { AtSign, Mail, ShieldCheck, ExternalLink } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import api from '../lib/api';

let _userCache = null;
let _userCacheAt = 0;
const CACHE_TTL = 5 * 60 * 1000;

async function fetchUsers() {
  const now = Date.now();
  if (_userCache && now - _userCacheAt < CACHE_TTL) return _userCache;
  try {
    const r = await api.get('/users');
    _userCache = (r.data.users || r.data || []).map(u => ({
      id: u.id || u.email,
      name: u.full_name || u.email,
      email: u.email,
      role: u.role,
    }));
    _userCacheAt = now;
  } catch {
    _userCache = [];
  }
  return _userCache;
}

function roleColor(role) {
  switch (role) {
    case 'admin': return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'manager': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'restricted_user': return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'trainer': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    default: return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}

// Walk the text and split into segments of {type: 'text'|'mention', value, user?}
function tokenize(text, users) {
  if (!text) return [];
  // Sort user names by descending length so longest matches win (handles "John Doe" vs "John")
  const sorted = [...users].sort((a, b) => b.name.length - a.name.length);

  const out = [];
  let i = 0;
  let buf = '';
  while (i < text.length) {
    if (text[i] === '@' && (i === 0 || /\s/.test(text[i - 1]))) {
      // Try to match a user name at this position
      let matched = null;
      for (const u of sorted) {
        const candidate = text.slice(i + 1, i + 1 + u.name.length);
        if (candidate === u.name) {
          // Must be followed by end/space/punctuation
          const next = text[i + 1 + u.name.length];
          if (next === undefined || /[\s.,!?;:)\]]/.test(next)) {
            matched = u;
            break;
          }
        }
      }
      if (matched) {
        if (buf) { out.push({ type: 'text', value: buf }); buf = ''; }
        out.push({ type: 'mention', value: `@${matched.name}`, user: matched });
        i += 1 + matched.name.length;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  if (buf) out.push({ type: 'text', value: buf });
  return out;
}

function MentionChip({ user }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 mx-0.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 text-[11px] font-medium border border-violet-200 transition-colors cursor-pointer align-baseline"
          data-testid={`mention-chip-${user.id}`}
        >
          <AtSign className="w-2.5 h-2.5" />
          {user.name}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
            {user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">{user.name}</div>
            {user.role && (
              <span className={`inline-flex items-center gap-1 mt-0.5 text-[9px] px-1.5 py-0.5 rounded-full border ${roleColor(user.role)}`}>
                <ShieldCheck className="w-2.5 h-2.5" />{user.role.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>
        {user.email && (
          <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-slate-500">
            <Mail className="w-3 h-3 flex-shrink-0" />
            <a href={`mailto:${user.email}`} className="truncate hover:text-violet-700 hover:underline">{user.email}</a>
          </div>
        )}
        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
          <span className="text-[9px] text-slate-400 italic">prototype</span>
          <button
            className="text-[10px] text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1"
            onClick={() => { /* future: open user profile / direct message */ }}
          >
            View profile <ExternalLink className="w-2.5 h-2.5" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function MentionText({ text, className = '' }) {
  const [users, setUsers] = useState([]);
  useEffect(() => { fetchUsers().then(setUsers); }, []);

  const segments = useMemo(() => tokenize(text || '', users), [text, users]);

  if (!text) return null;
  // While users load, render the raw text so layout doesn't jump much
  if (users.length === 0) return <span className={className}>{text}</span>;

  return (
    <span className={className}>
      {segments.map((seg, idx) =>
        seg.type === 'mention'
          ? <MentionChip key={idx} user={seg.user} />
          : <span key={idx}>{seg.value}</span>
      )}
    </span>
  );
}
